'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import searchItems from '@/services/search/searchItems';
import type { SearchResultItem, SearchResults } from '@/types/search';

// Stable empty fallback, so `items` keeps its identity between renders while
// there is no data and anything memoized on it stays memoized.
const NO_ITEMS: SearchResultItem[] = [];

/**
 * Runs `searchItems` for a term and reports the states a result surface needs.
 *
 * Shared by the header popover and the `/search` page, which differ in how they
 * present results but must never differ in what a term returns — the whole
 * point of moving both onto one request.
 *
 * `requestIdRef` does what a query key would: only the newest request may write
 * state, so one that lands late can't overwrite a later term's results. Retry
 * is a counter rather than a re-set of `query`, because clearing and restoring
 * the term in one tick is batched into no change at all.
 *
 * Requests go out as soon as there is a term: `searchItems` sends its own
 * `x-app-authorization`, so it no longer waits for the runtime's `fetch` patch
 * (see `SEARCH_HEADERS`).
 */
/**
 * An outcome together with the term it belongs to.
 *
 * Carrying the term is what makes "still fetching" derivable instead of stored:
 * anything other than an outcome for *this* query means the answer isn't in
 * yet. Three independent `useState`s couldn't express that, because the effect
 * that set `isFetching` ran a render too late — the first render of `/search?q=…`
 * saw `isFetching: false` with no data, fell through every guard, and rendered
 * "0 results found" into the SSR HTML, where it stayed until hydration.
 */
type SettledSearch = {
  readonly query: string;
  readonly data: SearchResults | null;
  readonly isError: boolean;
};

const useSearchResults = (query: string) => {
  const [settled, setSettled] = useState<SettledSearch | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const requestIdRef = useRef(0);

  useEffect(() => {
    // Any character searches, matching the miniapp (`enabled: query.length > 0`).
    // The endpoint answers a single letter perfectly well — a floor here only
    // made the field feel dead for the first keystroke.
    if (!query) return undefined;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    searchItems(query)
      .then(results => {
        if (requestIdRef.current !== requestId) return;
        setSettled({ query, data: results, isError: false });
      })
      .catch(() => {
        // Superseded: the request that replaced this one owns the state now.
        if (requestIdRef.current !== requestId) return;
        setSettled({ query, data: null, isError: true });
      });

    return undefined;
  }, [query, retryCount]);

  // Clearing the outcome is what puts the surfaces back into their loading
  // state; the counter is what re-runs the effect, since re-setting the same
  // term is no change at all.
  const retry = useCallback(() => {
    setSettled(null);
    setRetryCount(count => count + 1);
  }, []);

  // An outcome for an older term is not an answer to this one — it reads as
  // pending, so a stale grid never sits under a new query.
  const answered = settled?.query === query ? settled : null;
  const data = answered?.data ?? null;
  const items = data?.items ?? NO_ITEMS;
  const isFetching = Boolean(query) && !answered;

  return {
    items,
    restrictionMessage: data?.restrictionMessage ?? null,
    isFetching,
    isError: Boolean(answered?.isError),
    // A query that returned nothing — distinct from the idle state before
    // anything was searched.
    hasNoResults: Boolean(data) && !items.length,
    retry,
  };
};

export default useSearchResults;
